import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { getWithParams } from 'helpers/endpointCalls';
import { endpoints } from 'endpoints.config';
import { Spinner } from 'components/spinner/Spinner';
import { EmailQuoteModal } from 'components/schoolDetailsForm/EmailQuoteModal';
import { navigate } from '@reach/router';
import { AppPath } from 'appConstants';
import { setMembershipOptInfo, selectQuotesId } from 'reducers/misc';
import { useDispatch, useSelector } from 'react-redux';

type MOListedOptionsProps = {
  setMembershipQErr: Dispatch<SetStateAction<boolean>>;
  membershipQuestions: any;
  setMembershipResultList: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<number | string>>;
};

export const MOListedOptions: React.FC<MOListedOptionsProps> = ({
  setMembershipQErr,
  membershipQuestions,
  setMembershipResultList,
  setCurrentStep,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [oneYearPackage, setOneYearPackage] = useState<any>(null);
  const [threeYearPackage, setThreeYearPackage] = useState<any>(null);
  const [quoteValidDate, setQuoteValidDate] = useState<any>(null);
  const [openQuoteFormModal, setOpenQuoteFormModal] = useState<boolean>(false);
  const [quoteBtnTxt, setQuoteBtnTxt] = useState<string>('Email quote');
  const [disableQuoteBtn, setDisableQuoteBtn] = useState<boolean>(false);
  const [vATTxt, setVATTxt] = useState<string>(' ');

  const dispatch = useDispatch();

  const quotesId: number | null = useSelector(selectQuotesId);

  useEffect(() => {
    loadData();
  }, []);

  const formattedInvoiceDate = (dateStr: Date) => {
    const today = dateStr;
    const yyyy = today.getFullYear();
    let mm: any = today.getMonth() + 1;
    let dd: any = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    const formattedDateStr = dd + '/' + mm + '/' + yyyy;

    return formattedDateStr;
  };

  const loadData = async () => {
    let subjectIdsParam: string = '';
    let vATTxtStr =
      membershipQuestions.selectedLocationLabel
        .toLowerCase()
        .indexOf('other') <= -1
        ? '*'
        : ' ';

    membershipQuestions.selectedSubject.map((id: number, index: number) => {
      subjectIdsParam +=
        index === 0 ? '?SubjectsIds=' + id : '&SubjectsIds=' + id;
      return id;
    });

    const result = await getWithParams(
      endpoints.public.subscription.getSubscriptionTypesAndPrices +
        subjectIdsParam,
      {
        QuotesId: quotesId,
        // This endpoint takes a single code; the quote holds both and prices against them.
        PromoCode:
          (membershipQuestions.promoCodes &&
            membershipQuestions.promoCodes[0]) ||
          '',
      }
    );

    // The quote and tiles promise VAT-exclusive prices, so every figure is net. The comparison's
    // own annual/lifetime fields are gross, so net comes from the two orders it wraps.
    const comparison = membershipQuestions.pricingSummary.comparison;
    const baseOrder = comparison.baseOrder;
    const comparedOrder = comparison.comparedOrder;
    const comparedYears =
      (comparedOrder.packageResults[0] &&
        comparedOrder.packageResults[0].subscriptionTypesDuration) ||
      3;

    const netForSubject = (order: any, shortName: string) =>
      (order.packageResults || [])
        .filter(
          (pkg: any) => (pkg.subjectsShortName || '').indexOf(shortName) > -1
        )
        .reduce((sum: number, pkg: any) => sum + pkg.netDiscounted, 0);

    const peOneYearNet = netForSubject(baseOrder, 'PE');
    const musicOneYearNet = netForSubject(baseOrder, 'Music');
    const peThreeYearAnnualNet =
      netForSubject(comparedOrder, 'PE') / comparedYears;
    const musicThreeYearAnnualNet =
      netForSubject(comparedOrder, 'Music') / comparedYears;
    const comparedAnnualNet = comparedOrder.totalNetDiscounted / comparedYears;
    const lifetimeNetSaving =
      baseOrder.totalNetDiscounted * comparedYears -
      comparedOrder.totalNetDiscounted;

    if (result.response && !result.errors) {
      let oneYearDiscountStr = peOneYearNet
        ? `PE: £${peOneYearNet.toFixed(2)}${vATTxtStr}`
        : '';
      oneYearDiscountStr += musicOneYearNet
        ? (peOneYearNet ? ', ' : '') +
          `MUSIC: £${musicOneYearNet.toFixed(2)}${vATTxtStr}`
        : '';

      let threeYearDiscountStr =
        peThreeYearAnnualNet && membershipQuestions.selectedSubject.length > 1
          ? `PE price per year: £${peThreeYearAnnualNet.toFixed(2)}${vATTxtStr}`
          : '';
      threeYearDiscountStr +=
        musicThreeYearAnnualNet &&
        membershipQuestions.selectedSubject.length > 1
          ? (peThreeYearAnnualNet ? ', ' : '') +
            `MUSIC price per year: £${musicThreeYearAnnualNet.toFixed(2)}${vATTxtStr}`
          : '';
      threeYearDiscountStr +=
        lifetimeNetSaving > 0
          ? ` (saves £${lifetimeNetSaving.toFixed(2)})`
          : '';

      let oneYearPackageObj = {
        discountDetails: oneYearDiscountStr,
        price: baseOrder.totalNetDiscounted,
        pricePerYear: null,
        subscriptionDuration: 1,
        subscriptionType: '1 Year',
        packageIds: baseOrder.packageResults.map((pkg: any) => pkg.packagesID),
      };

      let threeYearPackageObj = {
        discountDetails: threeYearDiscountStr,
        price: comparedOrder.totalNetDiscounted,
        pricePerYear: 'Price per year £' + comparedAnnualNet.toFixed(2),
        subscriptionDuration: comparedYears,
        subscriptionType: '3 Years',
        packageIds: comparedOrder.packageResults.map(
          (pkg: any) => pkg.packagesID
        ),
      };

      setOneYearPackage(oneYearPackageObj);
      setThreeYearPackage(threeYearPackageObj);
      setQuoteValidDate(result.response.quoteValidDate);
      setVATTxt(vATTxtStr);
      setLoading(false);
    } else {
      setMembershipQErr(true);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <>
          <div className="ResultingPackageList">
            {oneYearPackage && threeYearPackage && (
              <div className="PackageBlock">
                <span className="ConfirmIcon"></span>
                <h2>Thank you!</h2>
                <p>
                  Your subscription options for{' '}
                  {membershipQuestions.ageGroup +
                    ' ' +
                    membershipQuestions.selectedSubjectName}{' '}
                  are:
                </p>
                <p className="PackageDetailsLine">
                  <span className="BigTxt">
                    £{oneYearPackage.price.toFixed(2)}
                    {vATTxt} per year
                  </span>{' '}
                  {membershipQuestions.selectedSubject.length > 1
                    ? oneYearPackage.discountDetails
                    : null}
                </p>
                <p className="PackageDetailsLine">
                  or{' '}
                  <span className="BigTxt">
                    £{threeYearPackage.price.toFixed(2)}
                    {vATTxt} for three years
                  </span>{' '}
                  {threeYearPackage.discountDetails}
                </p>
                {quoteValidDate && (
                  <p>
                    This quote is valid until{' '}
                    {formattedInvoiceDate(new Date(quoteValidDate))}.
                  </p>
                )}
                {membershipQuestions.selectedLocationLabel
                  .toLowerCase()
                  .indexOf('other') <= -1 ? (
                  <p className="SmTxt">
                    *Prices exclude VAT but include any promotional code you
                    have specified
                  </p>
                ) : (
                  <p className="SmTxt">
                    *Prices include any promotional code you have specified
                  </p>
                )}
                <div
                  className="Btn BtnOrange"
                  onClick={() => {
                    dispatch(
                      setMembershipOptInfo([
                        {
                          selectedLocation:
                            membershipQuestions.selectedLocation,
                          selectedLocationLabel:
                            membershipQuestions.selectedLocationLabel,
                          selectedPackage: membershipQuestions.selectedPackage,
                          selectedSubject: membershipQuestions.selectedSubject,
                          selectedSubjectName:
                            membershipQuestions.selectedSubjectName,
                          ageGroup: membershipQuestions.ageGroup,
                          pupilsOnRoll: membershipQuestions.pupilsOnRoll,
                          promoCodes: membershipQuestions.promoCodes || [],
                          oneYearPackage: oneYearPackage,
                          threeYearPackage: threeYearPackage,
                          vatCountry:
                            membershipQuestions.selectedLocationLabel.toLowerCase() ===
                            'non-uk eu'
                              ? membershipQuestions.selectedEUCountry.value
                              : membershipQuestions.selectedLocationLabel.toLowerCase() ===
                                  'other'
                                ? membershipQuestions.selectedOtherCountry.value
                                : membershipQuestions.unitedKingdom[0].id,
                          vatCode: membershipQuestions.vatCode || null,
                        },
                      ])
                    );
                    // The id in the URL, so the page runs off the quote as the email link does.
                    navigate(AppPath.SUBSCRIBE + '?id=' + quotesId);
                  }}
                >
                  Subscribe now
                </div>
                <div
                  className={
                    'Btn BtnWhiteOrange' +
                    (disableQuoteBtn ? ' DisableQuoteBtn' : '')
                  }
                  onClick={() => setOpenQuoteFormModal(true)}
                >
                  {quoteBtnTxt}
                </div>
              </div>
            )}
          </div>

          <div
            className="Btn BtnWhite ResPkgBackBtn"
            onClick={() => {
              setCurrentStep(5);
              setMembershipResultList(false);
            }}
          >
            Back
          </div>
        </>
      )}

      {openQuoteFormModal && (
        <EmailQuoteModal
          panelOpen={openQuoteFormModal}
          togglePanelOpen={setOpenQuoteFormModal}
          membershipQuestions={membershipQuestions}
          setMembershipQErr={setMembershipQErr}
          setQuoteBtnTxt={setQuoteBtnTxt}
          setDisableQuoteBtn={setDisableQuoteBtn}
        />
      )}
    </>
  );
};
